import { FunctionComponent, useState } from 'react'
import styles from './daycareList.module.scss'
import Title from '@/components/_custom/Title/title'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import LinkApiClientDaycareDialog from './Dialog/linkApiClientDaycareDialog.tsx'
import { ApiClientDaycare } from '@/types/ApiClient.ts'
import Desktop from '@/modules/desktopMobile/desktop.tsx'
import { QuickSearchToolbar, StyledDataGrid } from '@/components/_custom/DataGrid'
import { frFR } from '@mui/x-data-grid/locales'
import { GridColDef, GridRowClassNameParams } from '@mui/x-data-grid'
import Mobile from '@/modules/desktopMobile/mobile.tsx'
import Close from '@mui/icons-material/Close'
import LinkOff from '@mui/icons-material/LinkOff'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { daycareTypeToString } from '@/modules/helper/daycareHelper.ts'
import UnlinkApiClientDaycareDialog from '@/container/settings/apiClient/apiClientProfile/daycareList/Dialog/unlinkApiClientDaycareDialog.tsx'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage.tsx'

interface DaycareApiClientListProps {
    id: number
    apiClientDaycares: Array<ApiClientDaycare>
    refetch: () => void
}

const DaycareApiClientList: FunctionComponent<DaycareApiClientListProps> = ({
    id,
    apiClientDaycares,
    refetch
}: DaycareApiClientListProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [isLinkDaycareDialogOpen, setLinkDaycareDialogOpen] = useState(false)
    const [isUnlinkDaycareDialogOpen, setUnlinkDaycareDialogOpen] = useState(false)

    const [unlinkedDaycare, setUnlinkedDaycare] = useState<ApiClientDaycare | null>(null)

    function unlinkOneDaycare(daycare: ApiClientDaycare): void {
        setUnlinkDaycareDialogOpen(true)
        setUnlinkedDaycare(daycare)
    }

    const headerClassName: string = styles.HeaderDaycare

    const daycareColumnsWeb: GridColDef[] = [
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            width: 160,
            minWidth: 160,
            maxWidth: 300,
            flex: 1
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 160,
            minWidth: 160,
            maxWidth: 300,
            flex: 1,
            renderCell: (params) => (
                <a
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    href={`/daycare?daycare=${params.row.id}`}
                    onClick={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            navigate(`/daycare?daycare=${params.row.id}`)
                        }
                    }}
                >
                    {params.row.name}
                </a>
            )
        },
        {
            field: 'type',
            headerName: t('type'),
            headerClassName: headerClassName,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>{daycareTypeToString(params.row.type, t, true)}</div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 160,
            maxWidth: 160,
            minWidth: 160,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 400,
            maxWidth: 800,
            minWidth: 250,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <LinkOff onClick={() => unlinkOneDaycare(params.row)} className={styles.Icon} />
                    <a
                        href={`/daycare?daycare=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/daycare?daycare=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                </div>
            )
        }
    ]

    const daycareColumnsMobile: GridColDef[] = [
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            flex: 1
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            flex: 1,
            renderCell: (params) => (
                <div
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    onClick={() => navigate(`/daycare?daycare=${params.row.id}`)}
                >
                    {params.row.name}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <LinkOff onClick={() => unlinkOneDaycare(params.row)} className={styles.Icon} />
                    <VisibilityIcon
                        onClick={() => navigate(`/daycare?daycare=${params.row.id}`)}
                        className={styles.Icon}
                    />
                </div>
            ),
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        }
    ]

    return (
        <div className={styles.Container}>
            <div className={styles.TitleAndButton}>
                <div className={styles.Title}>
                    <Title text={t('structureList')} />
                </div>
                <ButtonTextIcon type={'add'} onClick={() => setLinkDaycareDialogOpen(true)} className={styles.Button} />
                {isLinkDaycareDialogOpen && (
                    <LinkApiClientDaycareDialog
                        open={isLinkDaycareDialogOpen}
                        handleClose={() => setLinkDaycareDialogOpen(false)}
                        daycares={apiClientDaycares}
                        id={id}
                        refetch={refetch}
                    />
                )}
            </div>
            {apiClientDaycares.length === 0 ? (
                <div className={styles.NoDaycare}>
                    <ErrorMessage error={t('noDaycare')} className={styles.Error} />
                </div>
            ) : (
                <>
                    <Desktop>
                        <StyledDataGrid
                            autoHeight
                            columns={daycareColumnsWeb}
                            rows={apiClientDaycares}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            getRowClassName={(params: GridRowClassNameParams): string => {
                                return `daycare-active-${params.row.active}`
                            }}
                            paginationMode={'server'}
                            rowCount={apiClientDaycares.length}
                        />
                    </Desktop>
                    <Mobile>
                        <StyledDataGrid
                            autoHeight
                            columns={daycareColumnsMobile}
                            rows={apiClientDaycares}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            paginationMode={'server'}
                            rowCount={apiClientDaycares.length}
                        />
                    </Mobile>
                </>
            )}
            {isUnlinkDaycareDialogOpen && unlinkedDaycare && (
                <UnlinkApiClientDaycareDialog
                    open={isUnlinkDaycareDialogOpen}
                    handleClose={() => {
                        setUnlinkedDaycare(null)
                        setUnlinkDaycareDialogOpen(false)
                    }}
                    daycare={unlinkedDaycare}
                    id={id}
                    refetch={refetch}
                />
            )}
        </div>
    )
}

export default DaycareApiClientList
